import React, { useState, useEffect, useRef, Suspense } from 'react';
import { Routes, Route, Link, NavLink, useNavigate, Navigate, useParams, useLocation } from 'react-router-dom';
import { FaBars, FaBell, FaTimes, FaEdit, FaBook, FaCalendarAlt, FaStickyNote, FaClipboardCheck, FaAward, FaTools, FaUsers, FaCommentDots, FaUser, FaUserPlus, FaHome } from 'react-icons/fa';
import AppHeader from './components/header/AppHeader';
import { isAdmin, isParent } from './utils/roles';
import { CircularProgress } from '@mui/material';

// ── Core (always loaded) ──
import Home from './pages/Home';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardLayout from './components/layout/DashboardLayout';
import AdminDashboardLayout from './components/layout/AdminDashboardLayout';
import ProtectedRoute from './components/ProtectedRoute';
import { AuthProvider, useAuth } from './components/AuthContext';
import ThemeSettingsModal from './components/ThemeSettingsModal';

// ── Lazy-loaded pages (code splitting) ──
const SavedPostsPage = React.lazy(() => import('./pages/SavedPostsPage'));
const SubjectList = React.lazy(() => import('./pages/SubjectList'));
const LessonList = React.lazy(() => import('./pages/LessonList'));
const LessonPage = React.lazy(() => import('./pages/LessonPage'));
const PracticePageV2 = React.lazy(() => import('./pages/PracticePageV2'));
const PracticeResultsPage = React.lazy(() => import('./pages/PracticeResultsPage'));
const PlanningDashboard = React.lazy(() => import('./pages/PlanningDashboard'));
const CalendarPage = React.lazy(() => import('./pages/CalendarPage'));
const PointsPage = React.lazy(() => import('./pages/PointsPage'));
const NotesPage = React.lazy(() => import('./pages/NotesPage'));
const SelfTestPage = React.lazy(() => import('./pages/SelfTestPage'));
const PostsPage = React.lazy(() => import('./pages/PostsPage'));
const ForgotPasswordPage = React.lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = React.lazy(() => import('./pages/ResetPasswordPage'));
const ActivateAccountPage = React.lazy(() => import('./pages/ActivateAccountPage'));
const ProfilePage = React.lazy(() => import('./pages/ProfilePage'));
const LeaderboardPage = React.lazy(() => import('./pages/LeaderboardPage'));
const AccountMembershipPage = React.lazy(() => import('./pages/AccountMembershipPage'));
const ManagePaymentMethodsPage = React.lazy(() => import('./pages/ManagePaymentMethodsPage'));
const RedeemCodePage = React.lazy(() => import('./pages/RedeemCodePage'));
const PaymentHistoryPage = React.lazy(() => import('./pages/PaymentHistoryPage'));
const WalletPaymentFormPage = React.lazy(() => import('./pages/WalletPaymentFormPage'));
const CardPaymentFormPage = React.lazy(() => import('./pages/CardPaymentFormPage'));
const CheckoutPage = React.lazy(() => import('./pages/CheckoutPage'));
const CheckoutStartPage = React.lazy(() => import('./pages/CheckoutStartPage'));
const AccessDenied = React.lazy(() => import('./pages/AccessDenied'));
const GroupsPage = React.lazy(() => import('./pages/GroupsPage'));
const ChatPage = React.lazy(() => import('./pages/ChatPage'));
const GroupCreatePage = React.lazy(() => import('./pages/GroupCreatePage'));
const GroupDetailPage = React.lazy(() => import('./pages/GroupDetailPage'));
const BlogListPage = React.lazy(() => import('./pages/BlogListPage'));
const BlogDetailPage = React.lazy(() => import('./pages/BlogDetailPage'));
const ExamPapersPage = React.lazy(() => import('./pages/ExamPapersPage'));
const ExamPaperSolvePage = React.lazy(() => import('./pages/ExamPaperSolvePage'));
const CallPage = React.lazy(() => import('./features/rtc/CallPage'));

// ── Lazy-loaded parent pages ──
const ParentDashboardLayout = React.lazy(() => import('./components/layout/ParentDashboardLayout'));
const ChildrenListPage = React.lazy(() => import('./pages/parent/ChildrenListPage'));
const ChildDetailPage = React.lazy(() => import('./pages/parent/ChildDetailPage'));
const LinkChildPage = React.lazy(() => import('./pages/parent/LinkChildPage'));

// ── Lazy-loaded admin pages ──
const AdminCreatePage = React.lazy(() => import('./pages/AdminCreatePage'));
const AdminUsersPage = React.lazy(() => import('./pages/AdminUsersPage'));
const AdminFeedbackPage = React.lazy(() => import('./pages/AdminFeedbackPage'));
const AdminSubscriptionsPage = React.lazy(() => import('./pages/AdminSubscriptionsPage'));
const AdminPaymentsPage = React.lazy(() => import('./pages/AdminPaymentsPage'));
const AdminPaymentMethodsPage = React.lazy(() => import('./pages/AdminPaymentMethodsPage'));
const PostsManagementPage = React.lazy(() => import('./pages/PostsManagementPage'));
const AdminSensitivePage = React.lazy(() => import('./pages/AdminSensitivePage'));
const AdminPointsPage = React.lazy(() => import('./pages/AdminPointsPage'));
const AdminMaintenancePage = React.lazy(() => import('./pages/AdminMaintenancePage'));
const AdminBlogsPage = React.lazy(() => import('./pages/AdminBlogsPage'));
const AdminExamPapersPage = React.lazy(() => import('./pages/AdminExamPapersPage'));
const AdminSubjectsPage = React.lazy(() => import('./pages/AdminSubjectsPage'));
const AdminSyncPage = React.lazy(() => import('./pages/AdminSyncPage'));
import { ThemeContext, applyThemeToDocument } from './components/ThemeContext';
import { getAuthToken } from './utils/auth';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import CssBaseline from '@mui/material/CssBaseline';
import { Snackbar, Alert, Button, Avatar, IconButton, Tooltip, Menu, MenuItem, Divider, ListItemIcon, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Stack, Typography, Box } from '@mui/material';
import { getAvatarUrl } from './utils/avatar';
import UserSearchDropdown from './components/UserSearchDropdown';
import NetworkIconButton from './components/network/NetworkIconButton';
import MyNetworkDrawer from './components/network/MyNetworkDrawer';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import EditIcon from '@mui/icons-material/Edit';
import Logout from '@mui/icons-material/Logout';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import PaymentMethodPickerDialog from './components/payments/PaymentMethodPickerDialog';
import SubscriptionPlansDialog from './components/SubscriptionPlansDialog';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import { useTranslation } from 'react-i18next';
import { io } from 'socket.io-client';
import { SOCKET_URL } from './config/env';
import LanguageSwitcher from './components/LanguageSwitcher';
import ChatSystem from './components/chat/ChatSystem';
import { ChatProvider, useChat } from './components/chat/ChatContext';
import ProfileDialog from './components/network/ProfileDialog';
import { CallProvider } from './features/rtc/CallContext';
import MiniCall from './features/rtc/MiniCall';
import { getUnreadCount, listNotifications, markRead, markAllRead } from './utils/notificationsApi';
import { acceptInvitation, declineInvitation } from './utils/groupsApi';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import FlagIcon from '@mui/icons-material/Flag';
import ChecklistIcon from '@mui/icons-material/Checklist';
import DoneAllIcon from '@mui/icons-material/DoneAll';

// Notification helpers and badge components moved to components/header/

function PracticeRouteElement() {
  const { lessonId } = useParams();
  const { t, i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  const [breadcrumbs, setBreadcrumbs] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const lang = (i18n && i18n.language || 'vi').toLowerCase().startsWith('en') ? 'en' : 'vi';

    const load = async () => {
      try {
        const token = getAuthToken();
        // Fetch lesson to get title and subjectId
        const lessonRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}/lessons/single/${lessonId}?includeAllLangs=true`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : undefined
        });
        const lessonData = await lessonRes.json();
        const lessonTitle = (lessonData?.i18n?.[lang]?.title) || (lessonData?.i18n?.vi?.title) || lessonData?.title || t('lesson.detail', 'Lesson');
        const subjectId = lessonData?.subjectId || lessonData?.subject?._id || lessonData?.subject?.id;

        let subjectName = null;
        if (subjectId) {
          try {
            const subjRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}/subjects/${subjectId}`, {
              headers: token ? { 'Authorization': `Bearer ${token}` } : undefined
            });
            if (subjRes.ok) {
              const subj = await subjRes.json();
              subjectName = subj?.name || null;
            }
          } catch (_) {
            // ignore subject fetch errors; fallback will apply
          }
        }

        const bc = [
          { label: t('nav.home','Home'), to: '/' },
          { label: t('nav.subjects','Subjects'), to: '/subjects' },
          subjectId && subjectName ? { label: subjectName, to: `/subjects/${subjectId}` } : null,
          { label: lessonTitle, to: `/lesson/${lessonId}` },
          { label: t('practice.title','Practice') }
        ].filter(Boolean);
        if (!cancelled) setBreadcrumbs(bc);
      } catch (err) {
        if (!cancelled) setBreadcrumbs(null); // will fallback
      }
    };

    if (lessonId) load();
    return () => { cancelled = true; };
  }, [lessonId, i18n && i18n.language, t]);

  const fallback = [{ label: t('nav.home','Home'), to: '/' }, { label: t('practice.title','Practice') }];
  const bc = breadcrumbs && Array.isArray(breadcrumbs) ? breadcrumbs : fallback;

  return (
    <ProtectedRoute isAuthenticated={isAuthenticated} requiredRoles={['student']}>
      <DashboardLayout
        title={t('practice.title', 'Practice')}
        breadcrumbs={bc}
      >
        <PracticePageV2 />
      </DashboardLayout>
    </ProtectedRoute>
  );
}

import ErrorBoundary from './components/common/ErrorBoundary';

function AppContent() {
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef(null);
  const { user, isAuthenticated, loading, refreshUser, updateUser, logout: authLogout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const startVideoCall = () => {
    try {
      // Generate a simple room id: 8-char base36 + timestamp suffix for uniqueness
      const rand = Math.random().toString(36).slice(2, 10);
      const roomId = `${rand}-${Date.now().toString(36)}`;
      setAccountAnchorEl(null);
      setAccountOpen(false);
      navigate(`/call/${roomId}`);
    } catch (_) {
      // fallback: navigate to a default room
      navigate(`/call/${Date.now().toString(36)}`);
    }
  };

  // Pricing modal visibility
  const [pricingOpen, setPricingOpen] = useState(false);
  // Pricing plans loaded from backend (public)
  const [pricingPlans, setPricingPlans] = useState(null);
  const [pricingPlansLoading, setPricingPlansLoading] = useState(false);
  // Provider selection dialog state
  const [providerDialogOpen, setProviderDialogOpen] = useState(false);
  const [pendingPlanType, setPendingPlanType] = useState(null);
  const [providerSubmitting, setProviderSubmitting] = useState(false);

  // Compute plan label based on explicit plan selection, active window, and backend badge
  const getPlanInfo = (u) => {
    if (!u) return { short: '', full: '' };
    const plan = u.planType ? String(u.planType) : '';
    const badge = u.planBadge ? String(u.planBadge) : '';

    const startAt = u.subscriptionStartAt ? new Date(u.subscriptionStartAt) : null;
    const endAtRaw = u.subscribedUntil ? new Date(u.subscribedUntil) : null;
    const endOfDay = (d) => {
      if (!d || isNaN(d.getTime())) return null;
      const x = new Date(d);
      x.setHours(23, 59, 59, 999);
      return x;
    };
    const endAt = endAtRaw;
    const endInclusive = endAt ? endOfDay(endAt) : null;
    const endFuture = !!(endInclusive && endInclusive.getTime() >= Date.now());

    const isPaidMonthlyActive = plan === 'PAID_MONTHLY' && endFuture;
    const isPaidYearlyActive = plan === 'PAID_YEARLY' && endFuture;
    const isFreeActive = plan === 'FREE' && endFuture;

    const hasDates = startAt && !isNaN(startAt.getTime()) && endAt && !isNaN(endAt.getTime());
    const datesText = hasDates ? ` (Start: ${startAt.toLocaleDateString()} — End: ${endAt.toLocaleDateString()})` : '';

    // Respect backend badges only when still active within date window
    if (badge === 'Pro' && (isPaidMonthlyActive || isPaidYearlyActive)) {
      return { short: t('subscription.proShort'), full: (plan === 'PAID_YEARLY' ? t('subscription.proYearly') : t('subscription.proMonthly')) + (hasDates ? datesText : '') };
    }
    if (badge === 'Trial' && endFuture) {
      const daysLeft = Math.max(0, Math.ceil(((endInclusive && !isNaN(endInclusive.getTime())) ? (endInclusive.getTime() - Date.now()) : 0) / (24*60*60*1000)));
      return { short: t('subscription.trialShort'), full: t('subscription.trialFull', { days: daysLeft }) };
    }

    // Prioritize active paid, then active free, else no plan
    if (isPaidYearlyActive) return { short: t('subscription.proShort'), full: t('subscription.proYearly') + datesText };
    if (isPaidMonthlyActive) return { short: t('subscription.proShort'), full: t('subscription.proMonthly') + datesText };
    if (isFreeActive) return { short: t('subscription.freeShort'), full: t('subscription.freeLimited') };
    return { short: t('subscription.noPlanShort'), full: t('subscription.noPlanFull') };
  };

  // Global network error snackbar state
  const [netErrorOpen, setNetErrorOpen] = useState(false);
  const [netErrorMessage, setNetErrorMessage] = useState('');

  // App feedback snackbar (replace native alert with MUI)
  const [appSnack, setAppSnack] = useState({ open: false, message: '', severity: 'success', duration: 4000 });
  const showSnack = (message, severity = 'success', duration = 4000) => setAppSnack({ open: true, message, severity, duration });

  // Confirm dialogs
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);

  // Upcoming badge state
  const [upcomingItems, setUpcomingItems] = useState([]);
  const [badgeCount, setBadgeCount] = useState(0);
  const [badgeOpen, setBadgeOpen] = useState(false);
  const badgeRef = useRef(null);
  const [dismissedKeys, setDismissedKeys] = useState(new Set());
  const [dismissedNotifIds, setDismissedNotifIds] = useState(new Set());
  const [badgeCleared, setBadgeCleared] = useState(false);

  // Admin feedback pending count (for notification badge)
  const [fbPendingCount, setFbPendingCount] = useState(0);
  const [fbBellOpen, setFbBellOpen] = useState(false);
  const [fbPendingLoading, setFbPendingLoading] = useState(false);
  // Notifications (mentions)
  const [notifCount, setNotifCount] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notifAnchorEl, setNotifAnchorEl] = useState(null);
  const [processingInviteId, setProcessingInviteId] = useState(null);
  const notifMenuOpen = Boolean(notifAnchorEl);
  const fbBellRef = useRef(null);
  const notifRef = useRef(null);

  // Calendar badge (meetings)
  const [calendarBadge, setCalendarBadge] = useState(0);
  const location = useLocation();

  // Account avatar dropdown state
  const [accountOpen, setAccountOpen] = useState(false);
  const [accountAnchorEl, setAccountAnchorEl] = useState(null);
  const accountMenuOpen = Boolean(accountAnchorEl);
  const accountRef = useRef(null);

  // Network feature state
  const [networkOpen, setNetworkOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileUser, setProfileUser] = useState(null);
  const [logoutHover, setLogoutHover] = useState(false);


  // Theme settings state
  const [themeOpen, setThemeOpen] = useState(false);
  const [themeSettings, setThemeSettings] = useState({
    theme_mode: 'light',
    accent_color: '#2b6cb0',
    font_size: 'normal',
    background_style: 'solid',
    theme_preset: null,
    mood: null,
    custom_background_url: null,
    custom_banner_url: null,
    updated_at: null,
  });

  const UPCOMING_DAYS = Number(process.env.REACT_APP_UPCOMING_DAYS || 3);

    // Initialize calendar badge from localStorage
    useEffect(() => {
      try {
        const saved = parseInt(localStorage.getItem('calendarBadge') || '0', 10);
        if (!isNaN(saved)) setCalendarBadge(saved);
      } catch (_) {}
    }, []);

    // Persist calendar badge to localStorage
    useEffect(() => {
      try { localStorage.setItem('calendarBadge', String(calendarBadge || 0)); } catch (_) {}
    }, [calendarBadge]);

    // Increment calendar badge on meeting events via notifications socket
    useEffect(() => {
      let socket;
      try {
        const token = getAuthToken();
        const base = SOCKET_URL || '';
        const url = base ? `${base}/notify` : '/notify';
        socket = io(url, {
          transports: ['websocket', 'polling'],
          auth: token ? { token } : undefined,
          withCredentials: true,
        });
        const myId = (() => { try { return user && (user._id || user.id || user.userId) ? String(user._id || user.id || user.userId) : ''; } catch (_) { return ''; } })();
        const inc = () => setCalendarBadge(prev => Math.min(999, (Number(prev) || 0) + 1));
        const incIfNotOwner = (payload) => {
          try {
            const createdBy = String(payload?.createdBy || payload?.meeting?.createdBy || '');
            if (myId && createdBy && myId === createdBy) return; // do not increment for owner-created events
          } catch (_) {}
          inc();
        };
        socket.on('meeting:assigned', incIfNotOwner);
        socket.on('meeting:updated', incIfNotOwner);
        socket.on('meeting:cancelled', inc);
        socket.on('meeting:reminder', (payload) => {
          try {
            const minutes = Number(payload?.minutesLeft) || 0;
            const title = payload?.meeting?.title || '';
            const msg = minutes > 0
              ? t('calendar.meeting.reminder','Meeting starts in {{minutes}} minutes: {{title}}',{ minutes, title })
              : t('calendar.meeting.startsNow','Your meeting is starting now: {{title}}',{ title });
            // Persist until user closes (only for meeting reminders)
            showSnack(msg, 'info', null);
          } catch (_) {}
          inc();
        });
        // When backend signals notifications changed, refresh unread count
        socket.on('notification:refresh', async () => {
          try {
            const cnt = await getUnreadCount().catch(()=>({ count: 0 }));
            setNotifCount(cnt?.count || 0);
          } catch (_) {}
        });
        // Also refresh unread count when window regains focus (fallback)
        let focusHandler;
        try {
          focusHandler = async () => {
            try {
              const cnt = await getUnreadCount().catch(()=>({ count: 0 }));
              setNotifCount(cnt?.count || 0);
            } catch (_) {}
          };
          window.addEventListener('focus', focusHandler);
        } catch (_) {}
      } catch (_) {}
      return () => {
        try { if (socket) socket.close(); } catch (_) {}
        try { if (focusHandler) window.removeEventListener('focus', focusHandler); } catch (_) {}
      };
    }, [user]);

    // Reset calendar badge when user visits the calendar page
    useEffect(() => {
      try {
        if (location && /^\/calendar(\/|$)?/.test(location.pathname)) {
          if (calendarBadge !== 0) setCalendarBadge(0);
        }
      } catch (_) {}
    }, [location]);

  // Close menus/dropdowns on outside click or focus shift
  useEffect(() => {
    function handleCloseByTarget(target) {
      // Ignore clicks/focuses inside MUI Menus/Popovers (they render in a portal)
      try {
        if (target && typeof target.closest === 'function') {
          if (target.closest('.MuiMenu-root, .MuiPopover-root, [role="menu"]')) return;
        }
      } catch (_) {}

      // On mobile Safari, doing state updates on touchstart before an input receives focus
      // can prevent the virtual keyboard from appearing. To avoid this, ignore touches/focus
      // that start on form controls or contenteditable elements.
      try {
        const tn = (target?.tagName || '').toLowerCase();
        if (tn === 'input' || tn === 'textarea' || tn === 'select' || target?.isContentEditable) {
          return;
        }
      } catch (_) {}

      if (moreRef.current && !moreRef.current.contains(target)) {
        setMoreOpen(false);
      }
      if (badgeRef.current && !badgeRef.current.contains(target)) {
        setBadgeOpen(false);
      }
      if (fbBellRef.current && !fbBellRef.current.contains(target)) {
        setFbBellOpen(false);
      }

      // If you still want to close menus globally, do it here (keep behavior consistent)
      if (accountRef.current && !accountRef.current.contains(target)) {
        setAccountOpen(false);
        setAccountAnchorEl(null);
      }
      if (notifRef.current && !notifRef.current.contains(target)) {
        setNotifOpen(false);
      }
    }

    function handlePointer(e) {
      handleCloseByTarget(e.target);
    }
    function handleFocusIn(e) {
      handleCloseByTarget(e.target);
    }
    function handleKey(e) {
      if (e.key === 'Escape') {
        setMoreOpen(false);
        setBadgeOpen(false);
        setFbBellOpen(false);
        setAccountOpen(false);
        setAccountAnchorEl(null);
        setNotifOpen(false);
      }
    }

    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('touchstart', handlePointer, { passive: true });
    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('touchstart', handlePointer);
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('keydown', handleKey);
    };
  }, []);

  // Patch global fetch to show friendly message when backend is unavailable
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.__appFetchPatched) return;

    const originalFetch = window.fetch?.bind(window);
    if (!originalFetch) return;

    function notifyNetworkIssue(detail) {
      try {
        window.dispatchEvent(new CustomEvent('global-network-error', { detail }));
      } catch (_) {
        // ignore
      }
    }

    window.fetch = async (...args) => {
      try {
        const res = await originalFetch(...args);
        // Also notify on service unavailable statuses to improve friendliness
        if (res && [502, 503, 504].includes(res.status)) {
          notifyNetworkIssue({
            message: 'Máy chủ đang bận hoặc tạm thời không khả dụng. Vui lòng thử lại sau ít phút.',
            status: res.status
          });
        }
        return res;
      } catch (err) {
        const msg = String(err?.message || '');
        const isNetworkError = err?.name === 'TypeError' || /Failed to fetch/i.test(msg) || /NetworkError/i.test(msg);
        if (isNetworkError) {
          notifyNetworkIssue({
            message: 'Hệ thống đang khởi động hoặc chưa sẵn sàng. Không thể kết nối tới máy chủ. Vui lòng thử lại sau một chút.',
            originalError: msg
          });
        }
        throw err;
      }
    };

    window.__appFetchPatched = true;
  }, []);

  // Listen for global network error events to open a Snackbar
  useEffect(() => {
    function onGlobalNetworkError(e) {
      const friendly = e?.detail?.message || 'Kết nối đến máy chủ gặp sự cố. Vui lòng thử lại sau.';
      setNetErrorMessage(friendly);
      setNetErrorOpen(true);
    }
    window.addEventListener('global-network-error', onGlobalNetworkError);
    return () => window.removeEventListener('global-network-error', onGlobalNetworkError);
  }, []);

  // Allow other pages to request opening of the pricing modal
  useEffect(() => {
    const onOpenPricing = async () => {
      try {
        // Refresh user from server before opening to ensure plan/trial flags are up-to-date
        await refreshUser();
      } catch (_) {
        // ignore refresh errors; modal can still open
      } finally {
        setPricingOpen(true);
      }
    };
    window.addEventListener('openPricing', onOpenPricing);
    return () => window.removeEventListener('openPricing', onOpenPricing);
  }, []);

  // Load pricing plans when pricing dialog opens (public endpoint)
  useEffect(() => {
    let cancelled = false;
    if (!pricingOpen) return;
    if (Array.isArray(pricingPlans) && pricingPlans.length > 0) return;
    const loadPlans = async () => {
      try {
        setPricingPlansLoading(true);
        const API_BASE = (process.env.REACT_APP_API_BASE_URL || '/api').replace(/\/+$/, '');
        const res = await fetch(`${API_BASE}/billing/plans`);
        const data = await res.json();
        if (!cancelled && res.ok && Array.isArray(data)) {
          setPricingPlans(data);
        }
      } catch (_) {
        // ignore
      } finally {
        if (!cancelled) setPricingPlansLoading(false);
      }
    };
    loadPlans();
    return () => { cancelled = true; };
  }, [pricingOpen]);

  // Fetch and apply theme settings when user logs in (canonical)
  useEffect(() => {
    const loadTheme = async () => {
      try {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        if (!user || !token) return;
        const API_BASE = (process.env.REACT_APP_API_BASE_URL || '/api').replace(/\/+$/, '');
        const res = await fetch(`${API_BASE}/user-settings`, { headers: { 'Authorization': `Bearer ${token}` } });
        if (res.ok) {
          const data = await res.json();
          setThemeSettings(data);
          applyThemeToDocument(data);
        }
      } catch (e) {
        // ignore
      }
    };
    loadTheme();
  }, [user]);

  // Re-apply theme when saved settings change
  useEffect(() => {
    applyThemeToDocument(themeSettings);
  }, [themeSettings]);

  const buildKey = (item) => `${item.type}:${item.id}`;
  const storageKeyForUser = (uid) => `dismissedUpcoming:${uid}`;
  const notifStorageKeyForUser = (uid) => `dismissedNotifs:${uid}`;

  // Load dismissed keys when user changes
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (!storedUser) {
      setDismissedKeys(new Set());
      setDismissedNotifIds(new Set());
      return;
    }
    try {
      const u = JSON.parse(storedUser);
      // Upcoming dismissal keys
      const raw = localStorage.getItem(storageKeyForUser(u.id));
      const arr = raw ? JSON.parse(raw) : [];
      setDismissedKeys(new Set(Array.isArray(arr) ? arr : []));
      // Notification dismissal ids
      const rawN = localStorage.getItem(notifStorageKeyForUser(u.id));
      const arrN = rawN ? JSON.parse(rawN) : [];
      setDismissedNotifIds(new Set(Array.isArray(arrN) ? arrN : []));
    } catch (e) {
      setDismissedKeys(new Set());
      setDismissedNotifIds(new Set());
    }
  }, [user]);

  const persistDismissed = (setObj) => {
    try {
      const storedUser = localStorage.getItem('user');
      if (!storedUser) return;
      const u = JSON.parse(storedUser);
      localStorage.setItem(storageKeyForUser(u.id), JSON.stringify(Array.from(setObj)));
    } catch {}
  };

  const persistDismissedNotifs = (setObj) => {
    try {
      const storedUser = localStorage.getItem('user');
      if (!storedUser) return;
      const u = JSON.parse(storedUser);
      localStorage.setItem(notifStorageKeyForUser(u.id), JSON.stringify(Array.from(setObj)));
    } catch {}
  };

  // Fetch upcoming tasks/goals when user is available and when updates occur
  const fetchUpcoming = React.useCallback(async () => {
    try {
      const token = getAuthToken();
      const storedUser = localStorage.getItem('user') || sessionStorage.getItem('user');
      if (!token || !storedUser) return;
      const u = JSON.parse(storedUser);
      if (!u || !u.id) return;

      const headers = { 'Authorization': `Bearer ${token}` };
      const ts = Date.now();
      const [tasksRes, goalsRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_BASE_URL}/tasks/user/${u.id}?_=${ts}`, { headers }),
        fetch(`${process.env.REACT_APP_API_BASE_URL}/goals/user/${u.id}?_=${ts+1}`, { headers })
      ]);
      if (!tasksRes.ok || !goalsRes.ok) {
        return;
      }
      const [tasks, goals] = await Promise.all([tasksRes.json(), goalsRes.json()]);

      const now = new Date();
      const inDays = (dateStr) => {
        if (!dateStr) return Infinity;
        const d = new Date(dateStr);
        // Normalize to local end-of-day to include today correctly
        d.setHours(23, 59, 59, 999);
        const diffMs = d.getTime() - now.getTime();
        return Math.floor(diffMs / (1000 * 60 * 60 * 24));
      };

      const upcomingTasks = (Array.isArray(tasks) ? tasks : (tasks?.tasks || [])).filter(t => {
        const days = inDays(t.dueDate);
        const status = String(t.status || '').toLowerCase();
        return Number.isFinite(days) && days >= 0 && days <= UPCOMING_DAYS && status !== 'completed' && status !== 'cancelled';
      }).map(t => ({
        type: 'task',
        id: t._id,
        title: t.title || 'Công việc',
        date: t.dueDate,
        url: '/planning'
      }));

      const upcomingGoals = (Array.isArray(goals) ? goals : (goals?.goals || [])).filter(g => {
        const days = inDays(g.targetDate);
        const status = String(g.status || '').toLowerCase();
        return Number.isFinite(days) && days >= 0 && days <= UPCOMING_DAYS && status !== 'achieved' && status !== 'abandoned';
      }).map(g => ({
        type: 'goal',
        id: g._id,
        title: g.title || 'Mục tiêu',
        date: g.targetDate,
        url: `/goals/${g._id}`
      }));

      const combined = [...upcomingTasks, ...upcomingGoals].sort((a, b) => new Date(a.date) - new Date(b.date));
      setUpcomingItems(combined);
    } catch (e) {
      // Silent fail for header badge
      console.warn('Failed to fetch upcoming items', e);
    }
  }, [UPCOMING_DAYS]);

  useEffect(() => {
    if (user) {
      fetchUpcoming();
    }
  }, [user, fetchUpcoming]);

  // Recalculate badge count based on visible items and cleared flag
  useEffect(() => {
    const visible = upcomingItems.filter(it => !dismissedKeys.has(buildKey(it)));
    if (badgeCleared) {
      // keep at zero until new data arrives
      setBadgeCount(0);
    } else {
      setBadgeCount(visible.length);
    }
  }, [upcomingItems, dismissedKeys, badgeCleared]);

  // Listen to cross-page updates to refresh upcoming items
  useEffect(() => {
    const onUpdate = () => { setBadgeCleared(false); fetchUpcoming(); };
    const onStorage = (e) => {
      if (e.key === 'lastTasksGoalsUpdate') {
        setBadgeCleared(false);
        fetchUpcoming();
      }
    };
    window.addEventListener('tasksGoalsUpdated', onUpdate);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('tasksGoalsUpdated', onUpdate);
      window.removeEventListener('storage', onStorage);
    };
  }, [fetchUpcoming]);

  // Admin feedback pending count: fetch on demand (no auto polling)
  const fetchPendingFb = React.useCallback(async () => {
    try {
      const token = getAuthToken();
      const storedUser = localStorage.getItem('user') || sessionStorage.getItem('user');
      if (!token || !storedUser) { setFbPendingCount(0); return false; }
      const u = JSON.parse(storedUser);
      if (!(u?.role === 'admin' || (Array.isArray(u?.roles) && u.roles.includes('admin')))) { setFbPendingCount(0); return false; }
      const API_BASE = (process.env.REACT_APP_API_BASE_URL || '/api').replace(/\/+$/, '');
      const params = new URLSearchParams({ status: 'pending', page: '1', limit: '1', _: String(Date.now()) });
      const res = await fetch(`${API_BASE}/feedback/admin?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) { setFbPendingCount(0); return false; }
      const data = await res.json();
      const total = Number(data?.total || 0);
      setFbPendingCount(Number.isFinite(total) ? total : 0);
      return true;
    } catch (_) {
      setFbPendingCount(0);
      return false;
    }
  }, [user]);

  // Ensure initial pending feedback count is loaded for admins (no polling)
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (!storedUser) return;
    try {
      const u = JSON.parse(storedUser);
      if (u?.role === 'admin' || (Array.isArray(u?.roles) && u.roles.includes('admin'))) {
        fetchPendingFb();
      }
    } catch {}
  }, [fetchPendingFb]);

  // Handle logout
  const handleLogout = () => {
    setAccountOpen(false);
    authLogout();
  };

  // Build MUI theme from ThemeContext settings
  const resolveMode = () => {
    try {
      if (themeSettings?.theme_mode === 'system' && typeof window !== 'undefined' && window.matchMedia) {
        return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      }
      return (themeSettings?.theme_mode === 'dark' || themeSettings?.theme_mode === 'light') ? themeSettings.theme_mode : 'light';
    } catch (_) {
      return 'light';
    }
  };
  const mode = resolveMode();
  const primaryMain = themeSettings?.accent_color || '#1976d2';
  const muiTheme = createTheme({
    palette: {
      mode,
      primary: { main: primaryMain }
    },
    shape: {
      borderRadius: 12
    },
    typography: {
      fontFamily: 'Inter, Roboto, "Helvetica Neue", Arial, system-ui, -apple-system, Segoe UI, "Apple Color Emoji", "Segoe UI Emoji", sans-serif',
      button: {
        textTransform: 'none',
        fontWeight: 600
      }
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          html: {
            backgroundColor: 'var(--bg, #f6f8fb)'
          },
          body: {
            backgroundColor: 'var(--bg, #f6f8fb) !important',
            color: 'var(--text, #222222) !important',
            margin: 0,
            WebkitTextSizeAdjust: '100%'
          },
          '#root': {
            backgroundColor: 'var(--bg, #f6f8fb) !important',
            minHeight: '100vh'
          },
          'img, video, canvas, iframe': {
            maxWidth: '100%',
            height: 'auto',
            display: 'block'
          }
        }
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true
        },
        styleOverrides: {
          root: {
            borderRadius: 10
          }
        }
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 14,
            boxShadow: mode === 'dark' ? '0 8px 24px rgba(0,0,0,0.4)' : '0 6px 18px rgba(0,0,0,0.08)'
          }
        }
      },
      MuiLink: {
        styleOverrides: {
          root: { cursor: 'pointer' }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: 'none' }
        }
      },
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: 10 }
        }
      },
      MuiTextField: {
        defaultProps: {
          variant: 'outlined'
        }
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: { borderRadius: 10 }
        }
      },
      MuiContainer: {
        styleOverrides: {
          maxWidthXl: {
            maxWidth: '1600px'
          }
        }
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: 12,
            padding: 4,
            minWidth: 220
          }
        }
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontSize: '0.98rem',
            paddingTop: 10,
            paddingBottom: 10,
            paddingLeft: 14,
            paddingRight: 14,
            borderRadius: 8,
            minHeight: 40,
            '& .MuiListItemIcon-root': {
              minWidth: 32,
              marginRight: 8,
              color: 'inherit'
            },
            '&.Mui-selected': {
              backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'
            },
            '&.Mui-selected:hover': {
              backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'
            }
          }
        }
      }
    }
  });

  // Responsive breakpoints for header elements
  const isMdDown = useMediaQuery(muiTheme.breakpoints.down('md'));

  return (
      <ThemeContext.Provider value={{ settings: themeSettings, setSettings: setThemeSettings, openModal: () => setThemeOpen(true), closeModal: () => setThemeOpen(false) }}>
      <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      <CallProvider>
      <ChatProvider>
        <AppHeader
          user={user}
          loading={loading}
          isAdminPanel={typeof window !== 'undefined' && /(^|\/)admin(\/|$)/.test(window.location.pathname)}
          isMdDown={isMdDown}
          setProfileUser={setProfileUser}
          setProfileOpen={setProfileOpen}
          networkOpen={networkOpen}
          setNetworkOpen={setNetworkOpen}
          notifRef={notifRef}
          notifAnchorEl={notifAnchorEl}
          notifMenuOpen={notifMenuOpen}
          setNotifAnchorEl={setNotifAnchorEl}
          setNotifOpen={setNotifOpen}
          notifCount={notifCount}
          setNotifCount={setNotifCount}
          badgeCount={badgeCount}
          setBadgeCount={setBadgeCount}
          setBadgeCleared={setBadgeCleared}
          notifications={notifications}
          setNotifications={setNotifications}
          dismissedNotifIds={dismissedNotifIds}
          setDismissedNotifIds={setDismissedNotifIds}
          persistDismissedNotifs={persistDismissedNotifs}
          processingInviteId={processingInviteId}
          setProcessingInviteId={setProcessingInviteId}
          upcomingItems={upcomingItems}
          dismissedKeys={dismissedKeys}
          setDismissedKeys={setDismissedKeys}
          persistDismissed={persistDismissed}
          buildKey={buildKey}
          UPCOMING_DAYS={UPCOMING_DAYS}
          fbBellRef={fbBellRef}
          fbPendingCount={fbPendingCount}
          fbBellOpen={fbBellOpen}
          fbPendingLoading={fbPendingLoading}
          setFbBellOpen={setFbBellOpen}
          fetchPendingFb={fetchPendingFb}
          setFbPendingLoading={setFbPendingLoading}
          accountRef={accountRef}
          accountAnchorEl={accountAnchorEl}
          accountMenuOpen={accountMenuOpen}
          setAccountAnchorEl={setAccountAnchorEl}
          setAccountOpen={setAccountOpen}
          getPlanInfo={getPlanInfo}
          onOpenPricing={() => { try { window.dispatchEvent(new Event('openPricing')); } catch (_) { setPricingOpen(true); } }}
          setThemeOpen={setThemeOpen}
          handleLogout={handleLogout}
          calendarBadge={calendarBadge}
          navigate={navigate}
          showSnack={showSnack}
          t={t}
        />
        <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}><CircularProgress /></Box>}>
        <Routes>
          <Route path="/" element={(() => {
            if (isAdmin(user)) return <Navigate to="/admin/create" replace />;
            if (isParent(user)) return <Navigate to="/parent/children" replace />;
            return user ? (<DashboardLayout><Home /></DashboardLayout>) : (<Home />);
          })()} />
          
          {/* Protected routes that require authentication */}
          <Route path="/subjects" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} requiredRoles={['student']}>
              <DashboardLayout
                title={t('nav.subjects', 'Subjects')}
                breadcrumbs={[{ label: t('nav.home','Home'), to: '/' }, { label: t('nav.subjects', 'Subjects') }]}
              >
                <SubjectList />
              </DashboardLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/subjects/:subjectId" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} requiredRoles={['student']}>
              <DashboardLayout
                title={t('lessons', 'Lessons')}
                breadcrumbs={[{ label: t('nav.home','Home'), to: '/' }, { label: t('nav.subjects', 'Subjects'), to: '/subjects' }, { label: t('lessons', 'Lessons') }]}
              >
                <LessonList />
              </DashboardLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/lesson/:lessonId" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} requiredRoles={['student']}>
              <DashboardLayout
                title={t('lesson.detail', 'Lesson')}
                breadcrumbs={[{ label: t('nav.home','Home'), to: '/' }, { label: t('nav.subjects','Subjects'), to: '/subjects' }, { label: t('lesson.detail', 'Lesson') }]}
              >
                <LessonPage />
              </DashboardLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/practice/:lessonId" element={<PracticeRouteElement />} />
          <Route path="/practice-v2" element={(
            <ProtectedRoute isAuthenticated={isAuthenticated} requiredRoles={['student']}>
              <DashboardLayout title={t('practice.v2Title', 'Practice V2')} breadcrumbs={[{ label: t('nav.home','Home'), to: '/' }, { label: 'Practice V2' }]}>
                <PracticePageV2 />
              </DashboardLayout>
            </ProtectedRoute>
          )} />
          
          <Route path="/call" element={(
            <DashboardLayout title={t('nav.call','Call')}>
              <CallPage />
            </DashboardLayout>
          )} />
          <Route path="/call/:roomId" element={(
            <DashboardLayout title={t('nav.call','Call')}>
              <CallPage />
            </DashboardLayout>
          )} />
          
          <Route path="/planning" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} requiredRoles={['student']}>
              <DashboardLayout
                title={t('planning.title', 'Personal Planning')}
                breadcrumbs={[{ label: t('nav.home','Home'), to: '/' }, { label: t('nav.planning','Planning') }]}
              >
                <PlanningDashboard />
              </DashboardLayout>
            </ProtectedRoute>
          } />

          <Route path="/calendar" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} requiredRoles={['student']}>
              <CalendarPage />
            </ProtectedRoute>
          } />
          
          <Route path="/goals/:goalId" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} requiredRoles={['student']}>
              <DashboardLayout
                title={t('planning.goalDetail', 'Goal Detail')}
                breadcrumbs={[{ label: t('nav.home','Home'), to: '/' }, { label: t('nav.planning','Planning'), to: '/planning' }, { label: t('planning.goalDetail','Goal Detail') }]}
              >
                <PlanningDashboard />
              </DashboardLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/notes" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} requiredRoles={['student']}>
              <DashboardLayout
                title={t('nav.notes', 'Notes')}
                breadcrumbs={[{ label: t('nav.home','Home'), to: '/' }, { label: t('nav.notes','Notes') }]}
              >
                <NotesPage />
              </DashboardLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/self-tests" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} requiredRoles={['student']}>
              <DashboardLayout
                title={t('nav.selfTests', 'Self Tests')}
                breadcrumbs={[{ label: t('nav.home','Home'), to: '/' }, { label: t('nav.selfTests','Self Tests') }]}
              >
                <SelfTestPage />
              </DashboardLayout>
            </ProtectedRoute>
          } />

          <Route path="/practice-results" element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <DashboardLayout
                title={t('practiceResults.title', 'Practice Results')}
                breadcrumbs={[{ label: t('nav.home','Home'), to: '/' }, { label: t('practiceResults.title', 'Practice Results') }]}
              >
                <PracticeResultsPage />
              </DashboardLayout>
            </ProtectedRoute>
          } />

          <Route path="/exam-papers" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} requiredRoles={['student']}>
              <DashboardLayout
                title={t('examPapers.student.title', 'Exam Papers')}
                breadcrumbs={[{ label: t('nav.home','Home'), to: '/' }, { label: t('examPapers.student.title','Exam Papers') }]}
              >
                <ExamPapersPage />
              </DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/exam-papers/:examId/solve" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} requiredRoles={['student']}>
              <DashboardLayout
                title={t('examPapers.solve.title', 'Exam Paper')}
                breadcrumbs={[{ label: t('nav.home','Home'), to: '/' }, { label: t('examPapers.student.title','Exam Papers'), to: '/exam-papers' }, { label: t('examPapers.solve.title','Solve') }]}
              >
                <ExamPaperSolvePage />
              </DashboardLayout>
            </ProtectedRoute>
          } />

          <Route path="/posts" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} requiredRoles={['student']}>
              <DashboardLayout
                title={t('nav.posts', 'Posts')}
                breadcrumbs={[{ label: t('nav.home','Home'), to: '/' }, { label: t('nav.posts','Posts') }]}
              >
                <PostsPage />
              </DashboardLayout>
            </ProtectedRoute>
          } />

          <Route path="/posts/saved" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} requiredRoles={['student']}>
              <DashboardLayout
                title={t('posts.saved.title','Saved Posts')}
                breadcrumbs={[{ label: t('nav.home','Home'), to: '/' }, { label: t('nav.posts','Posts'), to: '/posts' }, { label: t('posts.saved.title','Saved Posts') }]}
              >
                <SavedPostsPage />
              </DashboardLayout>
            </ProtectedRoute>
          } />

          <Route path="/points" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} requiredRoles={['student']}>
              <DashboardLayout
                title={t('points.title', 'Points')}
                breadcrumbs={[{ label: t('nav.home','Home'), to: '/' }, { label: t('points.title','Points') }]}
              >
                <PointsPage />
              </DashboardLayout>
            </ProtectedRoute>
          } />

          <Route path="/groups" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} requiredRoles={['student']}>
              <DashboardLayout
                title={t('nav.groups', 'Groups')}
                breadcrumbs={[{ label: t('nav.home','Home'), to: '/' }, { label: t('nav.groups','Groups') }]}
              >
                <GroupsPage />
              </DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/groups/create" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} requiredRoles={['student']}>
              <DashboardLayout
                title={t('groups.createTitle', 'Create group')}
                breadcrumbs={[{ label: t('nav.home','Home'), to: '/' }, { label: t('nav.groups','Groups'), to: '/groups' }, { label: t('groups.createTitle','Create group') }]}
              >
                <GroupCreatePage />
              </DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/groups/:groupId" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} requiredRoles={['student']}>
              <DashboardLayout
                title={t('groups.detailTitle', 'Group detail')}
                breadcrumbs={[{ label: t('nav.home','Home'), to: '/' }, { label: t('nav.groups','Groups'), to: '/groups' }, { label: t('groups.detailTitle','Group detail') }]}
              >
                <GroupDetailPage />
              </DashboardLayout>
            </ProtectedRoute>
          } />

          {/* Blog routes */}
          <Route path="/blog" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} requiredRoles={['student']}>
              <DashboardLayout
                title={t('nav.blog','Articles')}
                breadcrumbs={[{ label: t('nav.home','Home'), to: '/' }, { label: t('nav.blog','Articles') }]}
              >
                <BlogListPage />
              </DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/blog/:id" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} requiredRoles={['student']}>
              <DashboardLayout
                title={t('nav.blog','Articles')}
                breadcrumbs={[{ label: t('nav.home','Home'), to: '/' }, { label: t('nav.blog','Articles'), to: '/blog' }, { label: t('common.detail','Detail') }]}
              >
                <BlogDetailPage />
              </DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/b/:shortId" element={
            <DashboardLayout title={t('nav.blog','Articles')} breadcrumbs={[{ label: t('nav.home','Home'), to: '/' }, { label: t('nav.blog','Articles') }] }>
              <BlogDetailPage />
            </DashboardLayout>
          } />
          
          {/* Profile page */}
          <Route path="/leaderboard" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} requiredRoles={['student','teacher','admin']}>
              <DashboardLayout
                title={t('leaderboard.title','Leaderboard')}
                breadcrumbs={[{ label: t('nav.home','Home'), to: '/' }, { label: t('leaderboard.title','Leaderboard') }]}
              >
                <LeaderboardPage />
              </DashboardLayout>
            </ProtectedRoute>
          } />

          <Route path="/profile" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} requiredRoles={['student','teacher','admin','parent']}>
              {isParent(user) ? (
                <ParentDashboardLayout title={t('common.profile', 'Profile')}>
                  <ProfilePage />
                </ParentDashboardLayout>
              ) : (
                <DashboardLayout
                  title={t('common.profile', 'Profile')}
                  breadcrumbs={[{ label: t('nav.home','Home'), to: '/' }, { label: t('common.profile','Profile') }]}
                >
                  <ProfilePage />
                </DashboardLayout>
              )}
            </ProtectedRoute>
          } />

          {/* Checkout start (choose plan + method) */}
          <Route path="/checkout" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} requiredRoles={['student','teacher','admin']}>
              <DashboardLayout
                title={t('payment.chooseMethodTitle', 'Choose payment method')}
                breadcrumbs={[{ label: t('nav.home','Home'), to: '/' }, { label: t('payment.checkout','Checkout') }]}
              >
                <CheckoutStartPage />
              </DashboardLayout>
            </ProtectedRoute>
          } />

          {/* Checkout page */}
          <Route path="/checkout/:orderId" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} requiredRoles={['student','teacher','admin']}>
              <DashboardLayout
                title={t('payment.chooseMethodTitle', 'Choose payment method')}
                breadcrumbs={[{ label: t('nav.home','Home'), to: '/' }, { label: t('payment.checkout','Checkout') }]}
              >
                <CheckoutPage />
              </DashboardLayout>
            </ProtectedRoute>
          } />

          {/* Parent pages */}
          <Route path="/parent/children" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} requiredRoles={['parent']}>
              <ParentDashboardLayout title={t('parent.children', 'Con của tôi')}>
                <ChildrenListPage />
              </ParentDashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/parent/children/:childId" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} requiredRoles={['parent']}>
              <ParentDashboardLayout title={t('parent.childDetail', 'Chi tiết học sinh')}>
                <ChildDetailPage />
              </ParentDashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/parent/link" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} requiredRoles={['parent']}>
              <ParentDashboardLayout title={t('parent.linkChild', 'Liên kết con')}>
                <LinkChildPage />
              </ParentDashboardLayout>
            </ProtectedRoute>
          } />

          {/* Admin pages */}
          <Route path="/admin/create" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} requiredRoles={['admin','teacher']}>
              <AdminDashboardLayout title={t('header.admin.create', 'Bài học & Luyện tập')} breadcrumbs={[{ label: t('nav.home','Home'), to: '/' }, { label: t('header.admin.create','Bài học & Luyện tập') }]}>
                <AdminCreatePage />
              </AdminDashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/users" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} requiredRoles={['admin']}>
              <AdminDashboardLayout title={t('header.admin.users', 'Người dùng & Quyền hạn')} breadcrumbs={[{ label: t('nav.home','Home'), to: '/' }, { label: t('header.admin.users','Người dùng & Quyền hạn') }] }>
                <AdminUsersPage />
              </AdminDashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/feedback" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} requiredRoles={['admin']}>
              <AdminDashboardLayout title={t('header.admin.feedback', 'Phản hồi học sinh')} breadcrumbs={[{ label: t('nav.home','Home'), to: '/' }, { label: t('header.admin.feedback','Phản hồi học sinh') }] }>
                <AdminFeedbackPage />
              </AdminDashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/subscriptions" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} requiredRoles={['admin']}>
              <AdminDashboardLayout title={t('header.admin.subscriptions','Subscriptions')} breadcrumbs={[{ label: t('nav.home','Home'), to: '/' }, { label: t('header.admin.subscriptions','Subscriptions') }] }>
                <AdminSubscriptionsPage />
              </AdminDashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/payments" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} requiredRoles={['admin']}>
              <AdminDashboardLayout title={t('header.admin.payments','Payments')} breadcrumbs={[{ label: t('nav.home','Home'), to: '/' }, { label: t('header.admin.payments','Payments') }] }>
                <AdminPaymentsPage />
              </AdminDashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/payment-methods" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} requiredRoles={['admin']}>
              <AdminDashboardLayout title={t('header.admin.paymentMethods','Payment methods')} breadcrumbs={[{ label: t('nav.home','Home'), to: '/' }, { label: t('header.admin.paymentMethods','Payment methods') }] }>
                <AdminPaymentMethodsPage />
              </AdminDashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/subjects" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} requiredRoles={['admin']}>
              <AdminDashboardLayout title={t('header.admin.subjects','Subjects')} breadcrumbs={[{ label: t('nav.home','Home'), to: '/' }, { label: t('header.admin.subjects','Subjects') }] }>
                <AdminSubjectsPage />
              </AdminDashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/posts" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} requiredRoles={['admin']}>
              <PostsManagementPage />
            </ProtectedRoute>
          } />
          {/* Backward compatibility: redirect old path to new path */}
          <Route path="/admin/reports" element={<Navigate to="/admin/posts" replace />} />
          <Route path="/admin/sensitive" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} requiredRoles={['admin']}>
              <AdminDashboardLayout title={t('header.admin.sensitive','Sensitive')} breadcrumbs={[{ label: t('nav.home','Home'), to: '/' }, { label: t('header.admin.sensitive','Sensitive') }] }>
                <AdminSensitivePage />
              </AdminDashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/points" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} requiredRoles={['admin']}>
              <AdminDashboardLayout title={t('header.admin.points','Points & Rewards')} breadcrumbs={[{ label: t('nav.home','Home'), to: '/' }, { label: t('header.admin.points','Points & Rewards') }] }>
                <AdminPointsPage />
              </AdminDashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/maintenance" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} requiredRoles={['admin']}>
              <AdminMaintenancePage />
            </ProtectedRoute>
          } />
          <Route path="/admin/blogs" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} requiredRoles={['admin']}>
              <AdminBlogsPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/sync" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} requiredRoles={['admin']}>
              <AdminSyncPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/exam-papers" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} requiredRoles={['admin']}>
              <AdminExamPapersPage />
            </ProtectedRoute>
          } />
          {/* Access denied */}
          <Route path="/access-denied" element={<AccessDenied />} />
          {/* Authentication routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
                    <Route path="/activate/:code" element={<ActivateAccountPage />} />
                    <Route path="/activate" element={<ActivateAccountPage />} />
          <Route path="/account/membership" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} requiredRoles={['student','teacher','admin']}>
              <DashboardLayout
                title={t('membership.title', 'Membership')}
                breadcrumbs={[{ label: t('nav.home','Home'), to: '/' }, { label: t('membership.title','Membership') }]}
              >
                <AccountMembershipPage />
              </DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/account/payments/methods" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} requiredRoles={['student','teacher','admin']}>
              <DashboardLayout
                title={t('membership.managePayment', 'Manage payment method')}
                breadcrumbs={[{ label: t('nav.home','Home'), to: '/' }, { label: t('membership.managePayment','Manage payment method') }]}
              >
                <ManagePaymentMethodsPage />
              </DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/account/payments/methods/wallet" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} requiredRoles={['student','teacher','admin']}>
              <DashboardLayout
                title={t('membership.managePayment', 'Manage payment method')}
                breadcrumbs={[{ label: t('nav.home','Home'), to: '/' }, { label: t('membership.managePayment','Manage payment method') }]}
              >
                <WalletPaymentFormPage />
              </DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/account/payments/methods/card" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} requiredRoles={['student','teacher','admin']}>
              <DashboardLayout
                title={t('membership.managePayment', 'Manage payment method')}
                breadcrumbs={[{ label: t('nav.home','Home'), to: '/' }, { label: t('membership.managePayment','Manage payment method') }]}
              >
                <CardPaymentFormPage />
              </DashboardLayout>
            </ProtectedRoute>
          } />
          {/* Backward/alt path support */}
          <Route path="/payments/methods/card" element={<Navigate to="/account/payments/methods/card" replace />} />
          <Route path="/payments/methods/wallet" element={<Navigate to="/account/payments/methods/wallet" replace />} />
          <Route path="/t/payments/methods/wallet" element={<Navigate to="/account/payments/methods/wallet" replace />} />
          <Route path="/account/payments/redeem" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} requiredRoles={['student','teacher','admin']}>
              <DashboardLayout
                title={t('payments.picker.redeemLink', 'Redeem gift or promo code')}
                breadcrumbs={[{ label: t('nav.home','Home'), to: '/' }, { label: t('payments.picker.redeemLink','Redeem gift or promo code') }]}
              >
                <RedeemCodePage />
              </DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/account/payments/history" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} requiredRoles={['student','teacher','admin']}>
              <DashboardLayout
                title={t('membership.viewHistory', 'Payment history')}
                breadcrumbs={[{ label: t('nav.home','Home'), to: '/' }, { label: t('membership.viewHistory','Payment history') }]}
              >
                <PaymentHistoryPage />
              </DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/chat" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} requiredRoles={['student','teacher','admin']}>
              <DashboardLayout
                title={t('nav.chat','Chat')}
                breadcrumbs={[{ label: t('nav.home','Home'), to: '/' }, { label: t('nav.chat','Chat') }]}
              >
                <ChatPage />
              </DashboardLayout>
            </ProtectedRoute>
          } />
        </Routes>
        </Suspense>
        <ThemeSettingsModal
          isOpen={themeOpen}
          onClose={() => setThemeOpen(false)}
          initialSettings={themeSettings}
          onSave={async (s) => {
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            const API_BASE = (process.env.REACT_APP_API_BASE_URL || '/api').replace(/\/+$/, '');
            const res = await fetch(`${API_BASE}/user-settings`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify(s)
            });
            if (res.ok) {
              const data = await res.json();
              setThemeSettings(data);
              applyThemeToDocument(data);
            } else {
              throw new Error('Save failed');
            }
          }}
          onReset={async () => {
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            const API_BASE = (process.env.REACT_APP_API_BASE_URL || '/api').replace(/\/+$/, '');
            const res = await fetch(`${API_BASE}/user-settings`, {
              method: 'DELETE',
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!res.ok) throw new Error('Reset failed');
            const data = await res.json();
            setThemeSettings(data);
            applyThemeToDocument(data);
            return data;
          }}
        />
        {/* Global friendly network error snackbar */}
        <Snackbar
          open={netErrorOpen}
          onClose={() => setNetErrorOpen(false)}
          autoHideDuration={6000}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setNetErrorOpen(false)}
            severity="warning"
            variant="filled"
            sx={{ borderRadius: 2, boxShadow: 3, maxWidth: 420 }}
            action={
              <Button color="inherit" size="small" onClick={() => window.location.reload()}>
                Thử lại
              </Button>
            }
          >
            {netErrorMessage || 'Máy chủ chưa sẵn sàng. Vui lòng thử lại sau.'}
          </Alert>
        </Snackbar>

        {/* App-level feedback snackbar */}
        <Snackbar
          open={appSnack.open}
          onClose={() => setAppSnack(s => ({ ...s, open: false }))}
          autoHideDuration={appSnack.duration}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setAppSnack(s => ({ ...s, open: false }))}
            severity={appSnack.severity}
            variant="filled"
            sx={{ borderRadius: 2, boxShadow: 3, maxWidth: 480 }}
          >
            {appSnack.message}
          </Alert>
        </Snackbar>

        {(() => { const r = user?.role ? String(user.role).toLowerCase() : undefined; const rs = Array.isArray(user?.roles) ? user.roles.map(x => String(x).toLowerCase()) : (r ? [r] : []); if (rs.includes('admin')) return null; return (<>
        {/* Pricing / Plans modal */}
        <SubscriptionPlansDialog
          open={pricingOpen}
          onClose={() => setPricingOpen(false)}
          user={user}
          pricingPlans={pricingPlans}
          loading={pricingPlansLoading}
          onSelectPlan={(planType) => {
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            if (!token) { showSnack(t('protected.mustLogin'), 'warning'); return; }
            setPendingPlanType(planType);
            setProviderDialogOpen(true);
          }}
          onCancelSubscription={() => setCancelDialogOpen(true)}
          onActivateFree={async () => {
            try {
              const tk = localStorage.getItem('token') || sessionStorage.getItem('token');
              const API_BASE = (process.env.REACT_APP_API_BASE_URL || '/api').replace(/\/+$/, '');
              const res = await fetch(`${API_BASE}/auth/activate-free`, { method: 'POST', headers: { 'Authorization': `Bearer ${tk}` } });
              const data = await res.json();
              if (!res.ok) throw new Error(data?.error || 'Failed');
              const newUser = data?.user;
              if (newUser) {
                updateUser(newUser);
              }
              setPricingOpen(false);
              showSnack(t('subscription.activateFreeSuccess'), 'success');
            } catch (e) {
              showSnack(t('subscription.activateFreeFailed'), 'error');
            }
          }}
        />
        </> ); })()}

        {(() => { const r = user?.role ? String(user.role).toLowerCase() : undefined; const rs = Array.isArray(user?.roles) ? user.roles.map(x => String(x).toLowerCase()) : (r ? [r] : []); if (rs.includes('admin')) return null; return (<>
        {/* Provider selection dialog */}
        <PaymentMethodPickerDialog
          open={providerDialogOpen}
          onClose={() => (providerSubmitting ? null : setProviderDialogOpen(false))}
          planType={pendingPlanType}
          onSelect={async (prov) => {
            try {
              setProviderSubmitting(true);
              // Defer order creation until user confirms payment on checkout page
              setProviderDialogOpen(false);
              setPricingOpen(false);
              window.location.href = `/checkout/new`;
              try { sessionStorage.setItem('checkout.intent', JSON.stringify({ planType: pendingPlanType, provider: prov })); } catch (_) {}
            } catch (e) {
              showSnack(e?.message || t('payment.createPaymentFailed'), 'error');
            } finally {
              setProviderSubmitting(false);
            }
          }}
        />
        </> ); })()}

        {(() => { const r = user?.role ? String(user.role).toLowerCase() : undefined; const rs = Array.isArray(user?.roles) ? user.roles.map(x => String(x).toLowerCase()) : (r ? [r] : []); if (rs.includes('admin')) return null; return (<>
        {/* Cancel subscription confirmation dialog */}
        <Dialog open={cancelDialogOpen} onClose={() => (cancelLoading ? null : setCancelDialogOpen(false))} maxWidth="xs" fullWidth>
          <DialogTitle>{t('subscription.cancelSubscription')}</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" color="text.secondary">{t('subscription.cancelConfirmNoFree')}</Typography>
          </DialogContent>
          <DialogActions>
            <Button disabled={cancelLoading} onClick={() => setCancelDialogOpen(false)}>{t('common.cancel')}</Button>
            <Button
              variant="contained"
              color="error"
              disabled={cancelLoading}
              onClick={async () => {
                try {
                  setCancelLoading(true);
                  const tk = localStorage.getItem('token') || sessionStorage.getItem('token');
                  const API_BASE = (process.env.REACT_APP_API_BASE_URL || '/api').replace(/\/+$/, '');
                  const res = await fetch(`${API_BASE}/auth/cancel-subscription`, { method: 'POST', headers: { 'Authorization': `Bearer ${tk}` } });
                  const data = await res.json();
                  if (!res.ok) throw new Error(data?.error || 'Failed');
                  const newUser = data?.user;
                  if (newUser) {
                    updateUser(newUser);
                  }
                  showSnack(t('subscription.cancelSuccess'), 'success');
                  setCancelDialogOpen(false);
                  setPricingOpen(false);
                } catch (e) {
                  showSnack(t('subscription.cancelFailed'), 'error');
                } finally {
                  setCancelLoading(false);
                }
              }}
            >{t('common.confirm')}</Button>
          </DialogActions>
        </Dialog>
        </> ); })()}
      {/* Profile Dialog for selected search result */}
      {profileUser && (
        <ProfileDialog
          open={profileOpen}
          onClose={() => setProfileOpen(false)}
          user={profileUser}
          currentUser={user}
        />
      )}
        <MiniCall />
        <ChatSystem />
      </ChatProvider>
      </CallProvider>
      </ThemeProvider>
      </ThemeContext.Provider>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}
export default App;